/**
 * MonitoringAlertsSection — clinic health cards on the NiV morning briefing.
 *
 * One card per clinic (niv_alerts/{clinicId}, written daily by the
 * dailyAdminMonitoring cron). Each card carries: severity, a headline,
 * the signals behind it, facts (numbers), and a suggested action with a
 * ready WhatsApp message.
 *
 * "טופל" snoozes the card for 7 days (or 30). The cron resurfaces a snoozed
 * card only if the situation escalates. Info-only cards (opportunities,
 * conversions) sit in a collapsed group so they never compete with real work.
 */

import { useMemo, useState } from "react";
import {
  ArrowsClockwise,
  Bell,
  CaretDown,
  CaretLeft,
  Check,
  Checks,
  Lightbulb,
  User,
  WhatsappLogo,
} from "@phosphor-icons/react";
import PhoneNumber from "../../../../components/ui/PhoneNumber";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useSnoozeMonitoringAlertMutation,
  useSnoozeMonitoringAlertsBulkMutation,
} from "../../../../store/services/nivApi";
import { ALERT_SEVERITY_LABELS } from "../../../../services/nivFirebase";
import ClinicTags from "../shared/ClinicTags";
import { getRelativeDate } from "../shared/nivDerived";
import "./MonitoringAlertsSection.css";

const SNOOZE_WEEK = 7;
const SNOOZE_MONTH = 30;

const buildWhatsappLink = (phone, text) =>
  `https://wa.me/${String(phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(text || "")}`;

/** Compact facts line from the numbers the cron stored on the card. */
const buildFactsLine = (facts = {}) => {
  const parts = [];
  if (facts.lastLoginAt) parts.push(`כניסה אחרונה: ${getRelativeDate(facts.lastLoginAt)}`);
  if (facts.patientsCount != null) parts.push(`${facts.patientsCount} מטופלים`);
  if (facts.upcomingAppointments14 != null) {
    parts.push(`${facts.upcomingAppointments14} פגישות ב-14 הימים הבאים`);
  }
  if (facts.smsPct != null && facts.smsPct >= 50) {
    parts.push(`SMS ${facts.smsUsed}/${facts.smsLimit}`);
  }
  if (facts.aiPct != null && facts.aiPct >= 50) {
    parts.push(`AI ${facts.aiUsed}/${facts.aiLimit}`);
  }
  if (facts.trialDaysLeft != null) {
    parts.push(
      facts.trialDaysLeft > 0 ? `ניסיון: ${facts.trialDaysLeft} ימים נותרו` : "ניסיון הסתיים"
    );
  }
  return parts.join(" · ");
};

const daysSinceLabel = (iso) => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "מהיום";
  if (days === 1) return "מאתמול";
  return `כבר ${days} ימים`;
};

function MonitoringAlertsSection({ cards = [], rowsById = {}, isAuthorized, onOpenClinic }) {
  const { showSuccess, showError } = useToast();
  const [showInfo, setShowInfo] = useState(false);
  const [snoozeAlert, { isLoading: isSnoozing }] = useSnoozeMonitoringAlertMutation();
  const [snoozeBulk, { isLoading: isClearing }] = useSnoozeMonitoringAlertsBulkMutation();

  const { actionable, informational } = useMemo(() => {
    const actionable = [];
    const informational = [];
    (cards || []).forEach((card) => {
      if (card.severity === "critical" || card.severity === "warning") actionable.push(card);
      else informational.push(card);
    });
    return { actionable, informational };
  }, [cards]);

  if (actionable.length === 0 && informational.length === 0) return null;

  const handleSnooze = async (card, days) => {
    if (!isAuthorized) return;
    try {
      await snoozeAlert({ card, days }).unwrap();
      showSuccess(days === SNOOZE_MONTH ? "הקליניקה הושתקה לחודש" : "סומן כטופל — יחזור רק אם המצב יחמיר");
    } catch (error) {
      console.error("[CK-9864] Error snoozing clinic health card:", {
        errorCode: "CK-9864",
        errorMessage: error?.message,
        clinicId: card?.id,
        days,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בסימון ההתראה");
    }
  };

  const handleClearAll = async () => {
    if (!isAuthorized || actionable.length === 0) return;
    try {
      await snoozeBulk({ cards: actionable, days: SNOOZE_WEEK }).unwrap();
      showSuccess("כל ההתראות סומנו כטופלות לשבוע");
    } catch (error) {
      console.error("[CK-9872] Error bulk-snoozing clinic health cards:", {
        errorCode: "CK-9872",
        errorMessage: error?.message,
        count: actionable.length,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בניקוי ההתראות");
    }
  };

  const renderCard = (card) => {
    const row = rowsById[card.clinicId];
    const phone = card.ownerPhone || row?.ownerPhone || "";
    const ownerName = card.ownerName || row?.ownerName || "";
    const suggestion = card.suggestion || null;
    const factsLine = buildFactsLine(card.facts);
    const since = daysSinceLabel(card.firstSeenAt);
    const openTab = suggestion?.action === "manage" ? "manage" : undefined;

    return (
      <article key={card.id} className={`nivma-card is-${card.severity}`}>
        <div className="nivma-head">
          <span className="nivma-dot" aria-hidden="true" />
          <div className="nivma-title">
            <button
              type="button"
              className="nivma-name"
              onClick={() => onOpenClinic(card.clinicId)}
              title="פתח קליניקה"
            >
              {card.clinicName || row?.name || "קליניקה ללא שם"}
            </button>
            <div className="nivma-meta">
              {row && <ClinicTags row={row} compact />}
              {ownerName && (
                <span>
                  <User size={11} weight="fill" /> {ownerName}
                </span>
              )}
              {phone && (
                <PhoneNumber phone={phone} className="nivma-phone" iconSize={11} stopPropagation />
              )}
              {since && <span className="nivma-since">{since}</span>}
            </div>
          </div>
          <span className="nivma-sev-badge">
            {ALERT_SEVERITY_LABELS[card.severity] || card.severity}
          </span>
        </div>

        <p className="nivma-headline">{card.headline}</p>

        {card.resurfacedReason && (
          <span className="nivma-resurfaced">
            <ArrowsClockwise size={12} weight="bold" /> חזר אחרי סימון כטופל: {card.resurfacedReason}
          </span>
        )}

        {Array.isArray(card.signals) && card.signals.length > 0 && (
          <ul className="nivma-signals">
            {card.signals.map((signal) => (
              <li key={signal.key} className="nivma-signal">
                <span className={`nivma-chip is-${signal.severity}`}>{signal.label}</span>
                {signal.detail && <span className="nivma-signal-detail">{signal.detail}</span>}
              </li>
            ))}
          </ul>
        )}

        {factsLine && <div className="nivma-facts">{factsLine}</div>}

        {suggestion?.text && (
          <div className="nivma-suggest">
            <Lightbulb size={15} weight="fill" />
            <span>{suggestion.text}</span>
          </div>
        )}

        <div className="nivma-actions">
          <div className="nivma-actions-start">
            {suggestion?.whatsappText && phone && (
              <a
                className="nivma-btn nivma-btn-whatsapp"
                href={buildWhatsappLink(phone, suggestion.whatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsappLogo size={14} weight="fill" /> וואטסאפ
              </a>
            )}
            <button
              type="button"
              className="nivma-btn nivma-btn-open"
              onClick={() => onOpenClinic(card.clinicId, openTab)}
            >
              {openTab === "manage" ? "ניהול מסלול" : "פתח"}
              <CaretLeft size={12} />
            </button>
          </div>
          <div className="nivma-actions-end">
            <button
              type="button"
              className="nivma-btn nivma-btn-done"
              onClick={() => handleSnooze(card, SNOOZE_WEEK)}
              disabled={isSnoozing}
              title="מסתיר לשבוע — חוזר רק אם המצב מחמיר"
            >
              <Check size={14} weight="bold" /> טופל
            </button>
            <button
              type="button"
              className="nivma-btn nivma-btn-mute"
              onClick={() => handleSnooze(card, SNOOZE_MONTH)}
              disabled={isSnoozing}
              title="מסתיר לחודש — חוזר רק אם המצב מחמיר"
            >
              חודש
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section className="nivmb-section nivma-section">
      <div className="nivmb-section-head nivmb-head-alerts">
        <Bell size={20} weight="fill" />
        <h4>
          התראות ניטור{actionable.length > 0 ? ` (${actionable.length})` : ""}
        </h4>
        {actionable.length > 1 && (
          <button
            type="button"
            className="nivmb-head-link nivmb-head-clear-all"
            onClick={handleClearAll}
            disabled={isClearing}
            title="מסמן את כל ההתראות כטופלות לשבוע"
          >
            <Checks size={14} weight="bold" />
            {isClearing ? "מנקה…" : `נקה הכל (${actionable.length})`}
          </button>
        )}
      </div>

      {actionable.length > 0 && <div className="nivma-list">{actionable.map(renderCard)}</div>}

      {informational.length > 0 && (
        <div className="nivma-info-group">
          <button
            type="button"
            className={`nivma-info-toggle${showInfo ? " is-open" : ""}`}
            onClick={() => setShowInfo((v) => !v)}
            aria-expanded={showInfo}
          >
            <CaretDown size={12} weight="bold" />
            מידע והזדמנויות ({informational.length})
          </button>
          {showInfo && <div className="nivma-list">{informational.map(renderCard)}</div>}
        </div>
      )}
    </section>
  );
}

export default MonitoringAlertsSection;
